// Self-updating tab. A static export has no server push, and asking ~200 voters
// at a polling station to hard-refresh does not scale — during the Firebase
// project switch a whole room stayed stuck on the previous bundle because a
// loaded page cannot be replaced from the server.
//
// So the page checks for itself: /version.json is regenerated on every build
// (scripts/gen-version.mjs) and carries the id that is also compiled into this
// bundle. Different id => a newer deploy is live => reload.
//
// Safe because the bearer token, the chosen election and the ballot selection
// all live in localStorage (lib/token.ts, store/appStore.ts): a reload keeps
// the voter exactly where they were.

import { useApp } from "@/store/appStore";

const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID ?? "";
const POLL_MS = 60_000;

// The ConfirmationResult returned by signInWithPhoneNumber lives in a React ref
// inside PhoneAuthForm — it is not serializable, so it cannot be persisted.
// Reloading between "SMS sent" and "code entered" would throw that away and
// strand the voter with a code that no longer has a session to verify against.
function isMidOtp(): boolean {
  const s = useApp.getState().auth.status;
  return s === "sendingOtp" || s === "otpSent";
}

async function liveBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { buildId?: unknown };
    return typeof data.buildId === "string" ? data.buildId : null;
  } catch {
    return null; // offline or a blip: nothing to do, try again next tick
  }
}

async function checkOnce(): Promise<void> {
  if (!BUILD_ID || document.visibilityState !== "visible" || isMidOtp()) return;
  const live = await liveBuildId();
  if (live && live !== BUILD_ID) window.location.reload();
}

/** Starts the poll. Idempotent; returns a cleanup for the caller's effect. */
export function startAutoUpdate(): () => void {
  const timer = window.setInterval(() => void checkOnce(), POLL_MS);
  const onVisible = () => void checkOnce();
  document.addEventListener("visibilitychange", onVisible);
  void checkOnce();
  return () => {
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
